import React, { useState, useRef, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './VoiceDialer.css';
import useWebAudioVoiceEffects from '../hooks/useWebAudioVoiceEffects';
import useVoicemodMedia from '../hooks/useVoicemodMedia';
import { TelnyxRTC } from '@telnyx/webrtc';
import { voiceDialerConfig } from '../config/voiceDialerConfig';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { XMarkIcon, Bars3Icon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { GiDominoMask, GiRadarSweep, GiEchoRipples, GiSoundWaves, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";

// Navigation for sidebar (matching Dashboard.jsx structure with added Voice Dialer)
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
    { name: 'Voice Dialer', href: '/voice-dialer', icon: GiEchoRipples, current: pathname === '/voice-dialer' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AIVoiceDialer() {
  const [phone, setPhone] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Use Web Audio effects instead of Voicemod
  const { voices, isConnected: voiceEffectsConnected, applyVoiceEffect } = useWebAudioVoiceEffects();
  const { track: processedTrack, isAvailable: micAvailable, error: micError } = useVoicemodMedia();
  
  // Navigation items (matching Dashboard.jsx structure)
  const navigation = getFilteredNavigation(window.location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: window.location.pathname === '/settings' };
  
  const [call, setCall] = useState(null);
  const [isCalling, setIsCalling] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [timer, setTimer] = useState(0);
  const [telnyxClient, setTelnyxClient] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Initialize Telnyx client
  useEffect(() => {
    const initTelnyx = async () => {
      try {
        console.log('🔌 Initializing Telnyx client with config:', {
          username: voiceDialerConfig.telnyx.username,
          // Don't log password for security
        });

        // Try token authentication if available, otherwise use credentials
        const clientConfig = {
          ringToneFile: null,
          ringtoneFile: null,
        };
        
        // FORCE CREDENTIAL AUTHENTICATION - Do not use token authentication
        const useTokenAuth = false; // Set to true only if you have a valid JWT token
        
        if (useTokenAuth && voiceDialerConfig.telnyx.token && 
            voiceDialerConfig.telnyx.token !== null && 
            voiceDialerConfig.telnyx.token !== undefined && 
            voiceDialerConfig.telnyx.token !== 'null' &&
            voiceDialerConfig.telnyx.token !== 'YOUR_TELNYX_JWT_OR_SIP_TOKEN' &&
            voiceDialerConfig.telnyx.token.trim() !== '') {
          console.log('🔑 Using token authentication with token:', voiceDialerConfig.telnyx.token);
          clientConfig.login_token = voiceDialerConfig.telnyx.token;
        } else {
          console.log('🔑 Using credential authentication');
          console.log('🔑 Username:', voiceDialerConfig.telnyx.username);
          console.log('🔑 Connection ID:', voiceDialerConfig.telnyx.connectionId);
          // Don't log password for security, but check if it exists
          console.log('🔑 Password provided:', !!voiceDialerConfig.telnyx.password);
          
          clientConfig.login = voiceDialerConfig.telnyx.username;
          clientConfig.password = voiceDialerConfig.telnyx.password;
          clientConfig.connection_id = voiceDialerConfig.telnyx.connectionId;
        }
        
        const client = new TelnyxRTC(clientConfig);

        // Set remote element for audio/video (required by official docs)
        client.remoteElement = 'remoteMedia';

        client.on('telnyx.ready', () => {
          console.log('✅ Telnyx client ready - checking gateway state...');
          console.log('🔍 Client connected status:', client.connected);
          
          // Check immediately if client reports as connected
          setTimeout(() => {
            console.log('🔍 Checking connection after ready event, client.connected:', client.connected);
            if (client.connected && connectionStatus !== 'connected') {
              console.log('✅ Client reports connected immediately, setting status to connected');
              setConnectionStatus('connected');
            }
          }, 1000);
          
          // Set a longer timeout to fallback to connected if no gateway state received
          setTimeout(() => {
            if (connectionStatus === 'disconnected') {
              console.log('⚠️ No gateway state received after 10 seconds, checking client.connected:', client.connected);
              if (client.connected) {
                console.log('✅ Client reports connected, setting status to connected');
                setConnectionStatus('connected');
              } else {
                console.log('❌ Client reports not connected, keeping as disconnected');
              }
            }
          }, 10000);
        });

        client.on('telnyx.error', (error) => {
          setConnectionStatus('error');
          console.error('❌ Telnyx error:', error);
          
          // Enhanced error logging for debugging
          console.error('❌ Error details:', {
            error: error.error,
            message: error.message,
            sessionId: error.sessionId,
            code: error.code,
            reason: error.reason
          });
          
          // Check for specific authentication errors
          if (error.error && typeof error.error === 'object') {
            console.error('❌ Inner error object:', error.error);
            console.error('❌ Error type:', typeof error.error);
            console.error('❌ Error keys:', Object.keys(error.error));
          }
          
          // Show user-friendly error message
          if (error.error && error.error.message) {
            alert(`Telnyx Authentication Error: ${error.error.message}`);
          } else if (error.message) {
            alert(`Telnyx Error: ${error.message}`);
          } else {
            alert('Telnyx connection failed. Please check your credentials.');
          }
        });

        client.on('telnyx.socket.open', () => {
          console.log('🔌 Telnyx WebSocket opened');
        });

        client.on('telnyx.socket.close', (event) => {
          setConnectionStatus('disconnected');
          console.log('🔌 Telnyx WebSocket closed');
          console.log('🔌 Close event details:', {
            code: event?.code,
            reason: event?.reason,
            wasClean: event?.wasClean,
            timestamp: new Date().toISOString()
          });
          
          // Reset any ongoing call state
          setIsCalling(false);
          setIsConnecting(false);
          setCall(null);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            setTimer(0);
          }
          
          // Attempt to reconnect after a delay
          setTimeout(() => {
            if (client && !client.connected) {
              console.log('🔄 Attempting to reconnect...');
              setConnectionStatus('disconnected');
              client.connect().catch(err => {
                console.error('❌ Reconnection failed:', err);
              });
            }
          }, 5000);
        });

        client.on('telnyx.socket.error', (error) => {
          console.error('🔌 Telnyx WebSocket error:', error);
        });

        // Handle notifications as per official documentation
        client.on('telnyx.notification', (notification) => {
          console.log('🔔 Telnyx notification:', notification.type, notification);
          
          // Handle gateway state changes
          if (notification.type === 'gatewayState') {
            console.log('🚪 Gateway state notification:', notification);
            if (notification.state === 'REGED') {
              setConnectionStatus('connected');
              console.log('✅ Gateway registered successfully');
            } else if (notification.state === 'DOWN') {
              setConnectionStatus('error');
              console.warn('⚠️ Gateway is DOWN - check SIP connection configuration');
            } else {
              console.log('🔄 Gateway state:', notification.state);
            }
          } 
          // Handle call updates as per official docs
          else if (notification.type === 'callUpdate') {
            const currentCall = notification.call;
            console.log('📞 Call update:', currentCall.state, 'for call ID:', currentCall.id);
            
            // Update active call reference
            if (currentCall.state !== 'destroy' && currentCall.state !== 'hangup') {
              setCall(currentCall);
            }
          }
          // Other notification types
          else if (notification.type === 'clientReady') {
            console.log('👤 Client ready notification:', notification);
          } else {
            console.log('📢 Other notification:', notification.type, notification);
          }
        });

        setTelnyxClient(client);
        console.log('🔌 Connecting to Telnyx...');
        await client.connect();
        console.log('🔌 Telnyx connect() completed');
      } catch (error) {
        console.error('❌ Failed to initialize Telnyx:', error);
        setConnectionStatus('error');
      }
    };

    initTelnyx();

    return () => {
      // Cleanup will be handled in a separate effect
    };
  }, []);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (call) {
        try {
          call.hangup();
        } catch (error) {
          console.error('Error hanging up call during cleanup:', error);
        }
      }
      if (telnyxClient) {
        try {
          telnyxClient.off('telnyx.notification');
          telnyxClient.disconnect();
        } catch (error) {
          console.error('Error disconnecting Telnyx client:', error);
        }
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [call, telnyxClient]);

  const handleVoiceChange = async (effectID) => {
    setSelectedVoice(effectID);
    if (effectID && processedTrack) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      await applyVoiceEffect(stream, effectID);
    }
  };

  const dialPadNumbers = [
    { num: '1', letters: '' },
    { num: '2', letters: 'ABC' },
    { num: '3', letters: 'DEF' },
    { num: '4', letters: 'GHI' },
    { num: '5', letters: 'JKL' },
    { num: '6', letters: 'MNO' },
    { num: '7', letters: 'PQRS' },
    { num: '8', letters: 'TUV' },
    { num: '9', letters: 'WXYZ' },
  ];

  const handleDialPadClick = (digit) => {
    setPhone(prevPhone => prevPhone + digit);
  };

  const handleBackspace = () => {
    setPhone(prevPhone => prevPhone.slice(0, -1));
  };

  const handleCall = async () => {
    console.log('handleCall called - phone:', phone, 'telnyxClient connected:', telnyxClient?.connected);
    
    if (!telnyxClient || !phone) {
      alert('Please enter a phone number and ensure Telnyx connection is ready');
      return;
    }

    if (connectionStatus !== 'connected') {
      alert('Telnyx connection not ready. Status: ' + connectionStatus);
      return;
    }

    try {
      setIsConnecting(true);
      console.log('Starting call to:', phone);
      
      // Remote element is already set during client initialization
      console.log('Remote element already configured:', telnyxClient.remoteElement);

      // Request simple microphone access for outgoing calls (no voice effects)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        console.log('✅ Microphone access granted');
        
        // Clean up the test stream immediately to avoid conflicts
        stream.getTracks().forEach(track => track.stop());
      } catch (micError) {
        console.warn('⚠️ Microphone access denied:', micError);
        // Continue anyway - some calls might work without mic
      }

      // Create the call using Telnyx WebRTC
      const destinationNumber = phone.startsWith('+') ? phone : `+${phone}`;
      const callerNumber = voiceDialerConfig.telnyx.callerNumber;
      console.log('Calling destination:', destinationNumber);
      console.log('Using caller number:', callerNumber);
      console.log('Connection ID:', voiceDialerConfig.telnyx.connectionId);
      
      // Use official Telnyx WebRTC documentation pattern
      const callOptions = {
        destinationNumber: destinationNumber,  // Required: phone number or SIP URI
        callerNumber: callerNumber,            // Your purchased Telnyx phone number
        // Optional parameters from official docs
        audio: true,
        video: false
      };
      
      // Validate that callerNumber is in proper E.164 format
      if (!callerNumber.startsWith('+')) {
        console.error('❌ Caller number must be in +E.164 format:', callerNumber);
        alert(`Invalid caller number format: ${callerNumber}\nMust be in +E.164 format (e.g., +13239967539)`);
        setIsConnecting(false);
        return;
      }
      
      // Additional validation for E.164 format
      const e164Regex = /^\+[1-9]\d{1,14}$/;
      if (!e164Regex.test(callerNumber)) {
        console.error('❌ Caller number not in valid E.164 format:', callerNumber);
        alert(`Invalid E.164 format: ${callerNumber}\nMust be +[country code][number] with 7-15 digits total\nExample: +13239967539`);
        setIsConnecting(false);
        return;
      }
      
      console.log('✅ Caller number validation passed:', callerNumber);
      
      console.log('Call options:', callOptions);
      
      let newCall;
      try {
        console.log('🚀 Creating call with telnyxClient.newCall()...');
        newCall = telnyxClient.newCall(callOptions);
        console.log('Call created:', newCall);
        
        // Log all available properties on the call object
        console.log('📋 Available call properties:', Object.keys(newCall));
        console.log('📋 Call object type:', typeof newCall);
        console.log('📋 Call object constructor:', newCall.constructor.name);
        
        // Add a small delay to let the call object initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('Call details after delay:', {
          id: newCall.id,
          state: newCall.state,
          prevState: newCall.prevState,
          hangupCause: newCall.hangupCause,
          destinationNumber: newCall.destinationNumber,
          callerNumber: newCall.callerNumber,
          callerName: newCall.callerName,
          // Additional debugging
          direction: newCall.direction,
          options: callOptions,
          // Try alternative property names that might exist
          destination: newCall.destination,
          caller: newCall.caller,
          from: newCall.from,
          to: newCall.to
        });
      } catch (callError) {
        console.error('❌ Error creating call:', callError);
        setIsConnecting(false);
        alert(`Failed to create call: ${callError.message}`);
        return;
      }
      
      const callId = newCall.id;

      // Check if call was immediately destroyed (indicates configuration issue)
      if (newCall.state === 'destroy' || newCall.state === 'hangup') {
        console.error('❌ Call was immediately destroyed/hung up');
        console.error('Hangup cause:', newCall.hangupCause || newCall.cause);
        console.error('SIP Code:', newCall.sipCode);
        console.error('SIP Reason:', newCall.sipReason);
        console.error('Cause Code:', newCall.causeCode);
        console.error('Direction:', newCall.direction);
        console.error('Call State:', newCall.state);
        console.error('Previous State:', newCall.prevState);
        
        // Log SIP response codes for debugging (as requested by Telnyx support)
        if (newCall.sipCode) {
          console.error('🚨 SIP RESPONSE CODE DETECTED:', newCall.sipCode);
          if (newCall.sipCode === 403) {
            console.error('🚨 SIP 403 Forbidden - Check Telnyx documentation for D-codes');
            if (newCall.sipReason && newCall.sipReason.includes('D35')) {
              console.error('🚨 D35 Error: Invalid Caller Origination Number - FROM header must contain +E.164 format');
            }
          }
        }
        
        console.error('Full call object for Telnyx support:', newCall);
        setIsConnecting(false);
        
        let errorMsg = newCall.hangupCause || newCall.cause || 'Call failed immediately';
        
        // Check for specific SIP error codes
        if (newCall.sipCode === 403 && newCall.sipReason && newCall.sipReason.includes('D35')) {
          errorMsg = `SIP 403 D35 Error: Invalid Caller Origination Number\n\nThe FROM header in your SIP INVITE contains "${voiceDialerConfig.telnyx.username}" instead of a valid phone number.\n\nFIX: Your caller number "${callerNumber}" must be:\n• A valid phone number you own in Telnyx\n• In +E.164 format (starting with +)\n• Not your SIP username\n\nCheck your Telnyx Portal → Phone Numbers to verify you own "${callerNumber}"`;
        } else if (newCall.causeCode === 16) {
          errorMsg = `Call rejected with cause code 16 (NORMAL_CLEARING).\n\nThis is a SIP configuration issue. Your credentials work but outbound calling is not enabled.\n\nIMPORTANT: Go to Telnyx Portal → SIP Connections → Connection ${voiceDialerConfig.telnyx.connectionId} → Outbound tab and:\n• Check "Enable outbound calls"\n• Assign an "Outbound Voice Profile"\n• Save the configuration`;
        }
        alert(`Call failed: ${errorMsg}`);
        return;
      }

      // Listen for notifications instead of direct call events
      const handleNotification = (notification) => {
        console.log('📞 Call notification received:', notification);
        
        if (notification.type === 'callUpdate' && notification.call) {
          const notificationCallState = notification.call.state;
          const notificationCallId = notification.call.id;
          console.log('📞 Call state changed to:', notificationCallState, 'for call:', notificationCallId);
          
          // Check if this is our call
          if (callId === notificationCallId) {
            switch (notificationCallState) {
              case 'new':
                console.log('📞 Call created and ready');
                setIsConnecting(true);
                setIsCalling(false);
                break;
              case 'trying':
                console.log('📞 Call trying...');
                setIsConnecting(true);
                setIsCalling(false);
                break;
              case 'ringing':
                console.log('📞 Call ringing...');
                setIsConnecting(true);
                setIsCalling(false);
                break;
              case 'active':
                console.log('📞 Call connected!');
                setIsCalling(true);
                setIsConnecting(false);
                timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
                break;
              case 'hangup':
                console.log('📞 Call hung up');
                console.log('📞 Hangup details:', {
                  cause: notification.call.hangupCause || notification.call.cause,
                  causeCode: notification.call.causeCode,
                  sipCode: notification.call.sipCode,
                  sipReason: notification.call.sipReason
                });
                
                setIsCalling(false);
                setIsConnecting(false);
                clearInterval(timerRef.current);
                setTimer(0);
                setCall(null);
                telnyxClient.off('telnyx.notification', handleNotification);
                
                // Show why the call ended with SIP code if available
                let hangupMsg = notification.call.hangupCause || notification.call.cause || 'Call ended';
                if (notification.call.sipCode) {
                  hangupMsg += ` (SIP ${notification.call.sipCode})`;
                }
                alert(`Call ended: ${hangupMsg}`);
                break;
              case 'destroy':
                console.log('📞 Call destroyed - likely failed to connect');
                setIsCalling(false);
                setIsConnecting(false);
                clearInterval(timerRef.current);
                setTimer(0);
                setCall(null);
                telnyxClient.off('telnyx.notification', handleNotification);
                
                // Show error message for destroyed calls
                const errorMsg = notification.call.hangupCause || 'Call failed to connect. Please check the phone number and your Telnyx configuration.';
                alert(`Call failed: ${errorMsg}`);
                break;
                              default:
                  console.log('📞 Unknown call state:', notificationCallState);
              }
            } else {
              console.log('📞 Notification for different call:', notificationCallId);
          }
        } else {
          console.log('📞 Non-call notification:', notification.type, notification);
        }
      };

      // Listen for notifications
      telnyxClient.on('telnyx.notification', handleNotification);

      setCall(newCall);
      
      // Add a timeout to detect if call doesn't progress
      setTimeout(() => {
        if (isConnecting && !isCalling) {
          console.warn('Call seems stuck in connecting state');
          setIsConnecting(false);
          alert('Call failed to connect. Please check the phone number and try again.');
        }
      }, 30000); // 30 second timeout

    } catch (error) {
      console.error('Failed to start call:', error);
      setIsConnecting(false);
      alert('Failed to start call: ' + error.message);
    }
  };

  const handleHangUp = () => {
    if (call) {
      try {
        call.hangup();
      } catch (error) {
        console.error('Error hanging up call:', error);
      }
    }
    setIsCalling(false);
    setIsConnecting(false);
    clearInterval(timerRef.current);
    setTimer(0);
    setCall(null);
  };

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-400';
      case 'error': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'error': return 'Connection Error';
      default: return 'Connecting...';
    }
  };

  return (
    <>
      <div className="flex bg-gray-900 min-h-screen text-white">
        {/* Mobile menu */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 xl:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />
          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 ring-1 ring-white/10">
                <nav className="flex flex-1 flex-col justify-between">
                  <div>
                    <div className="flex mt-4 mb-4 items-center justify-center">
                      <img alt="Odyssey Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                    </div>
                    <ul role="list" className="mt-2 flex flex-col gap-y-1">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <a
                                href={item.href}
                                className={classNames(
                                  item.current
                                    ? 'bg-gray-800 text-white'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                  'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                )}
                              >
                                <item.icon aria-hidden="true" className="h-7 w-7 shrink-0" />
                                {item.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </div>
                  <div className="mb-4 flex justify-center">
                    <a
                      href={settingsNavItem.href}
                      className={classNames(
                        settingsNavItem.current ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                        'group flex gap-x-3 rounded-md p-3 text-sm font-semibold'
                      )}
                    >
                      <settingsNavItem.icon aria-hidden="true" className="h-7 w-7 shrink-0" />
                      <span className="sr-only">{settingsNavItem.name}</span>
                    </a>
                  </div>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static Sidebar for Desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-20 lg:overflow-y-auto lg:bg-slate-800 lg:pb-4">
          <nav className="flex flex-col justify-between h-full">
            <div>
              <div className="flex mt-4 mb-4 items-center justify-center">
                <img alt="Odyssey Relay" src="/images/logowhite.png" className="h-8 w-auto" />
              </div>
              <ul role="list" className="mt-2 flex flex-col items-center space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className={classNames(
                        item.current ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                        'group flex gap-x-3 rounded-md p-3 text-sm font-semibold'
                      )}
                    >
                      <item.icon aria-hidden="true" className="h-7 w-7 shrink-0" />
                      <span className="sr-only">{item.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mb-4 flex justify-center">
              <a
                href={settingsNavItem.href}
                className={classNames(
                  settingsNavItem.current ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                  'group flex gap-x-3 rounded-md p-3 text-sm font-semibold'
                )}
              >
                <settingsNavItem.icon aria-hidden="true" className="h-7 w-7 shrink-0" />
                <span className="sr-only">{settingsNavItem.name}</span>
              </a>
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 lg:pl-20">
          <div className="flex flex-col">
            {/* Mobile Menu Button */}
            <div className="flex items-center justify-between p-4 lg:hidden">
              <button 
                type="button" 
                onClick={() => setSidebarOpen(true)} 
                className="-m-2 p-3 text-white bg-gray-800 rounded-md hover:bg-gray-700"
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>

            {/* Voice Dialer Content */}
            <div className="flex items-center justify-center min-h-screen bg-gray-900 p-4">
              {/* Hidden audio element for WebRTC calls (required by Telnyx) */}
              <audio id="remoteMedia" autoPlay={true} style={{ display: 'none' }} />
              
              <div className="minimalist-phone">
                <h2 className="text-2xl font-semibold mb-6 text-center text-white">
                  🎤 Voice Effects Collection
                </h2>

                {/* Status indicators */}
                <div className="mb-4 space-y-2">
                  <div className="flex justify-between text-xs text-white">
                    <span>Voice Effects:</span>
                    <span className={voiceEffectsConnected ? 'text-green-400' : 'text-yellow-400'}>
                      {voiceEffectsConnected ? 'Ready' : 'Loading'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-white">
                    <span>Microphone:</span>
                    <span className={micAvailable ? 'text-green-400' : 'text-red-400'}>
                      {micError ? micError : micAvailable ? 'Available' : 'Not Available'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-white">
                    <span>Telnyx:</span>
                    <span className={getConnectionStatusColor()}>
                      {getConnectionStatusText()}
                    </span>
                  </div>
                </div>

                {/* Input Mode Toggle */}
                <div className="mb-4 flex justify-center">
                  <div className="bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setIsTyping(false)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        !isTyping ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Dial Pad
                    </button>
                    <button
                      onClick={() => setIsTyping(true)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        isTyping ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Type Number
                    </button>
                  </div>
                </div>

                {/* Voice Effects Selection */}
                <div className="mb-6">
                  <select
                    value={selectedVoice}
                    onChange={(e) => handleVoiceChange(e.target.value)}
                    className="w-full p-4 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none transition-all"
                  >
                    <option value="">🎤 Use Original Voice</option>
                    {voices.map((voice) => (
                      <option key={voice.id} value={voice.id}>
                        {voice.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-blue-300 mt-2 text-center opacity-70">
                    {voices.length} voice effects available
                  </p>
                </div>

                {/* Phone Input - Conditional Rendering */}
                {isTyping ? (
                  <div className="mb-4">
                    <PhoneInput
                      country="us"
                      value={phone}
                      onChange={setPhone}
                      containerClass="phone-input-container"
                      inputClass="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                      buttonClass="bg-gray-800 border border-gray-600 rounded-l-lg"
                      dropdownClass="bg-gray-800 text-white"
                      searchClass="bg-gray-700 text-white"
                      placeholder="Enter phone number"
                    />
                  </div>
                ) : (
                  <>
                    {/* Phone number display */}
                    <div className="mb-4 p-4 bg-gray-800 rounded-lg text-center">
                      <div className="text-2xl font-mono text-green-400 min-h-[2rem] flex items-center justify-center">
                        {phone || "Enter phone number"}
                      </div>
                    </div>

                    {/* Dial pad */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {dialPadNumbers.map((item) => (
                        <button
                          key={item.num}
                          onClick={() => handleDialPadClick(item.num)}
                          className="w-16 h-16 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xl font-semibold transition-colors flex flex-col items-center justify-center mx-auto"
                        >
                          <div>{item.num}</div>
                          {item.letters && (
                            <div className="text-xs opacity-75">{item.letters}</div>
                          )}
                        </button>
                      ))}
                      <button 
                        onClick={() => handleDialPadClick('*')} 
                        className="w-16 h-16 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xl font-semibold transition-colors flex items-center justify-center mx-auto"
                      >
                        *
                      </button>
                      <button 
                        onClick={() => handleDialPadClick('0')} 
                        className="w-16 h-16 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xl font-semibold transition-colors flex flex-col items-center justify-center mx-auto"
                      >
                        <div>0</div>
                        <div className="text-xs opacity-75">+</div>
                      </button>
                      <button 
                        onClick={() => handleDialPadClick('#')} 
                        className="w-16 h-16 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xl font-semibold transition-colors flex items-center justify-center mx-auto"
                      >
                        #
                      </button>
                    </div>
                  </>
                )}

                {/* Call controls */}
                <div className="flex space-x-4 mb-4 mt-6">
                  <button
                    onClick={handleBackspace}
                    className="flex-1 py-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-white text-xl"
                  >
                    ⌫
                  </button>
                  {!isCalling && !isConnecting ? (
                    <button
                      onClick={handleCall}
                      disabled={!phone || connectionStatus !== 'connected'}
                      className="flex-2 py-3 bg-green-600 rounded-lg hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors text-white font-medium"
                    >
                      📞 Call
                    </button>
                  ) : (
                    <button
                      onClick={handleHangUp}
                      className="flex-2 py-3 bg-red-600 rounded-lg hover:bg-red-500 transition-colors text-white font-medium"
                    >
                      {isConnecting ? 'Cancel' : '📞 Hang Up'}
                    </button>
                  )}
                </div>

                {/* Call status */}
                {isConnecting && (
                  <div className="text-center text-yellow-400 text-sm mb-2">
                    Connecting...
                  </div>
                )}
                
                {isCalling && (
                  <div className="text-center">
                    <div className="text-lg font-mono text-green-400 mb-2">
                      {formatTime(timer)}
                    </div>
                    <div className="text-sm text-gray-400">
                      Call in progress
                    </div>
                  </div>
                )}

                {/* Hidden audio element for remote audio */}
                <audio ref={audioRef} autoPlay className="hidden" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 